import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ToolResponse } from 'assistant-stream';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ToolCallMessagePartProps } from '@assistant-ui/react';

// Internal fields to filter from display columns
const INTERNAL_KEYS = new Set([
  'pk',
  'sk',
  'gsi1pk',
  'gsi1sk',
  'gsi2pk',
  'gsi2sk',
  '__edb_e__',
  '__edb_v__',
]);

function getDisplayColumns(item: Record<string, unknown>): string[] {
  const keys = Object.keys(item).filter((k) => !INTERNAL_KEYS.has(k) && k !== 'id');
  return keys.slice(0, 4);
}

function parseResult(result: unknown): Record<string, unknown>[] {
  if (Array.isArray(result)) return result as Record<string, unknown>[];
  if (typeof result === 'string') {
    try {
      const parsed = JSON.parse(result);
      if (Array.isArray(parsed)) return parsed as Record<string, unknown>[];
    } catch {
      // ignore parse error
    }
  }
  return [];
}

export const ListResultTool = ({
  toolName,
  argsText,
  result,
  status,
  addResult,
}: ToolCallMessagePartProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (status.type === 'running') {
    return (
      <Box display="flex" alignItems="center" gap={1} py={0.5}>
        <CircularProgress size={16} />
        <Typography variant="body2" fontStyle="italic">
          {t('aiChat.tool.running', { action: t(`aiChat.tool.name.${toolName}`, toolName) })}
        </Typography>
      </Box>
    );
  }

  if (status.type === 'requires-action') {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, p: 1 }}>
        <CardContent>
          <Typography variant="body2" mb={1}>
            {t('aiChat.tool.name.' + toolName, toolName)}
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="small"
              autoFocus
              onClick={() => {
                addResult(new ToolResponse({ result: 'confirmed' }));
              }}
            >
              {t('aiChat.tool.confirm')}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                addResult(new ToolResponse({ result: 'cancelled', isError: true }));
              }}
            >
              {t('aiChat.tool.cancel')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (status.type === 'incomplete') {
    return (
      <Box>
        <Chip
          icon={<ErrorOutlineIcon />}
          label={t('aiChat.tool.failed', { toolName: t(`aiChat.tool.name.${toolName}`, toolName) })}
          color="error"
          size="small"
          variant="outlined"
        />
        {argsText && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {argsText}
          </Typography>
        )}
      </Box>
    );
  }

  // Complete state: render table
  const rows = parseResult(result);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('aiChat.tool.list.empty')}
      </Typography>
    );
  }

  const columns = getDisplayColumns(rows[0]);
  const visibleRows = expanded ? rows : rows.slice(0, 5);
  const hasMore = rows.length > 5;

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col}>
                <Typography variant="caption" color="text.secondary">
                  {col}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row, i) => (
            <TableRow key={i}>
              {columns.map((col) => (
                <TableCell key={col}>
                  {/* eslint-disable-next-line @typescript-eslint/no-base-to-string */}
                  <Typography variant="body2">{String(row[col] ?? '')}</Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hasMore && !expanded && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', display: 'block', mt: 0.5 }}
          onClick={() => {
            setExpanded(true);
          }}
        >
          {t('aiChat.tool.showMore')}
        </Typography>
      )}
    </Box>
  );
};
